import type { ImageLoaderProps } from "next/image";

const ZONE_ORIGIN = "https://eurolabs-peptides.com";
const ZONE_HOSTS = new Set(["eurolabs-peptides.com", "www.eurolabs-peptides.com"]);
const API_HOST = "api.eurolabs-peptides.com";
const WIDTHS = [16, 32, 48, 64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1440, 1920, 2560];
const QUALITIES = [60, 75, 85];

function closest(value: number, candidates: readonly number[]): number {
	return candidates.reduce((current, candidate) =>
		Math.abs(candidate - value) < Math.abs(current - value) ? candidate : current,
	);
}

function transform(pathOrRemoteUrl: string, width: number, quality: number): string {
	return `${ZONE_ORIGIN}/cdn-cgi/image/width=${closest(width, WIDTHS)},quality=${closest(
		quality,
		QUALITIES,
	)},format=auto,anim=true/${pathOrRemoteUrl}`;
}

function invalidSource(): never {
	throw new Error("Cloudflare Images loader received a source that is not a safe image source");
}

function decodedPathname(url: URL): string {
	try {
		return decodeURIComponent(url.pathname);
	} catch {
		return invalidSource();
	}
}

function isBlockedPath(pathname: string): boolean {
	return pathname.includes("\\") || pathname.startsWith("/cdn-cgi/") || pathname.startsWith("/_next/image");
}

function isSvg(pathname: string): boolean {
	return pathname.toLowerCase().endsWith(".svg");
}

/**
 * Production-only Next Image loader for the Eurolabs Cloudflare zone.
 *
 * Local public assets and approved Eurolabs/Saleor media stay on the zone so
 * Cloudflare can transform them. Unknown HTTPS sources retain their original
 * URL: this preserves an existing image rather than proxying an arbitrary host
 * through the zone. Unsafe URLs and Cloudflare/Next optimization loops fail.
 */
export default function cloudflareImageLoader({ src, width, quality }: ImageLoaderProps): string {
	const resolvedQuality = quality ?? 75;

	if (
		src.startsWith("//") ||
		src.includes("\\") ||
		src.startsWith("/cdn-cgi/") ||
		src.startsWith("/_next/image")
	) {
		return invalidSource();
	}

	if (src.startsWith("/")) {
		const url = new URL(src, ZONE_ORIGIN);
		const pathname = decodedPathname(url);
		if (url.origin !== ZONE_ORIGIN || isBlockedPath(pathname)) return invalidSource();
		if (isSvg(pathname)) return `${url.pathname}${url.search}`;
		return transform(`${url.pathname.slice(1)}${url.search}`, width, resolvedQuality);
	}

	let url: URL;
	try {
		url = new URL(src);
	} catch {
		return invalidSource();
	}

	if (url.protocol !== "https:" || url.username || url.password || url.port) {
		return invalidSource();
	}

	if (ZONE_HOSTS.has(url.hostname)) {
		const pathname = decodedPathname(url);
		if (isBlockedPath(pathname)) return invalidSource();
		if (isSvg(pathname)) return url.href;
		return transform(`${url.pathname.slice(1)}${url.search}`, width, resolvedQuality);
	}

	if (
		url.hostname === API_HOST &&
		(url.pathname.startsWith("/media/") || url.pathname.startsWith("/thumbnail/"))
	) {
		if (isSvg(decodedPathname(url))) return url.href;
		return transform(url.href, width, resolvedQuality);
	}

	return url.href;
}

/**
 * Width the mobile product photo actually requests.
 * Next caps srcset density at 2×; a 412px phone therefore lands on 828,
 * the same slot `sizes="100vw"` selects. The gallery img uses this URL
 * with no larger srcset candidate.
 */
export const PRODUCT_MOBILE_LCP_WIDTH = 828;

export function productMobileLcpUrl(src: string): string {
	return cloudflareImageLoader({ src, width: PRODUCT_MOBILE_LCP_WIDTH, quality: 75 });
}
